import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { parkingService, CreateParkingDTO, ParkingFilters, ImportResult } from '@/services/parkingService';
import type { Parking, StatutParking, SituationParking } from '@/types';
import toast from 'react-hot-toast';
import { getErrorMessage } from '@/services/api';
import { ConfirmModal } from '@/components/common/Modal';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  FunnelIcon,
  MapPinIcon,
  ArrowUpTrayIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const STATUT_LABELS: Record<StatutParking, string> = {
  disponible: 'Disponible',
  en_deneignement: 'En deneignement',
  indisponible: 'Indisponible',
};

const STATUT_COLORS: Record<StatutParking, string> = {
  disponible: 'bg-green-100 text-green-800',
  en_deneignement: 'bg-orange-100 text-orange-800',
  indisponible: 'bg-red-100 text-red-800',
};

// SFD jalonnement Contact/Large (Oct 2026)
const SITUATION_LABELS: Record<SituationParking, string> = {
  contact: 'Contact',
  large: 'Large',
};

const SITUATION_COLORS: Record<SituationParking, string> = {
  contact: 'bg-blue-100 text-blue-800',
  large: 'bg-purple-100 text-purple-800',
};

/**
 * Page de gestion des parkings
 */
export function ParkingsPage() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<ParkingFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingParking, setEditingParking] = useState<Parking | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Parking | null>(null);
  const [statutChangeTarget, setStatutChangeTarget] = useState<{ parking: Parking; newStatut: StatutParking } | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importAeroport, setImportAeroport] = useState('');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Chargement des parkings
  const { data: parkings, isLoading } = useQuery({
    queryKey: ['parkings', filters],
    queryFn: () => parkingService.list(filters),
  });

  // Aeroports disponibles
  const { data: aeroports } = useQuery({
    queryKey: ['aeroports'],
    queryFn: () => parkingService.getAeroports(),
  });

  // Stats
  const { data: stats } = useQuery({
    queryKey: ['parkings-stats'],
    queryFn: () => parkingService.getStats(),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateParkingDTO) => parkingService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parkings'] });
      queryClient.invalidateQueries({ queryKey: ['parkings-stats'] });
      setShowModal(false);
      toast.success('Parking cree avec succes');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateParkingDTO> }) =>
      parkingService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parkings'] });
      setShowModal(false);
      setEditingParking(null);
      toast.success('Parking modifie avec succes');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => parkingService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parkings'] });
      queryClient.invalidateQueries({ queryKey: ['parkings-stats'] });
      toast.success('Parking supprime');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const updateStatutMutation = useMutation({
    mutationFn: ({ id, statut }: { id: number; statut: StatutParking }) =>
      parkingService.updateStatutHiver(id, statut),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parkings'] });
      queryClient.invalidateQueries({ queryKey: ['parkings-stats'] });
      toast.success('Statut mis a jour');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const importMutation = useMutation({
    mutationFn: ({ file, aeroport }: { file: File; aeroport?: string }) => parkingService.import(file, aeroport),
    onSuccess: (data) => {
      setImportResult(data);
      setImportFile(null);
      queryClient.invalidateQueries({ queryKey: ['parkings'] });
      queryClient.invalidateQueries({ queryKey: ['parkings-stats'] });
      queryClient.invalidateQueries({ queryKey: ['aeroports'] });
      const msg = `Import termine : ${data.created} cree(s), ${data.updated} mis a jour`;
      if (data.errors.length > 0) {
        toast.success(msg + ` (${data.errors.length} erreur(s))`);
      } else {
        toast.success(msg);
      }
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const handleImport = () => {
    if (importFile) {
      importMutation.mutate({ file: importFile, aeroport: importAeroport || undefined });
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const situationValue = formData.get('situation') as string;
    const latitudeValue = formData.get('latitude') as string;
    const longitudeValue = formData.get('longitude') as string;
    const data: CreateParkingDTO = {
      code: formData.get('code') as string,
      nom: formData.get('nom') as string || undefined,
      aeroport: formData.get('aeroport') as string,
      statutHiver: formData.get('statutHiver') as StatutParking,
      situation: (situationValue || undefined) as SituationParking | undefined,
      latitude: latitudeValue ? Number(latitudeValue) : null,
      longitude: longitudeValue ? Number(longitudeValue) : null,
    };

    if (editingParking) {
      updateMutation.mutate({ id: editingParking.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (parking: Parking) => {
    setDeleteTarget(parking);
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  const handleStatutChange = (parking: Parking, newStatut: StatutParking) => {
    if (newStatut !== parking.statutHiver) {
      setStatutChangeTarget({ parking, newStatut });
    }
  };

  const confirmStatutChange = () => {
    if (statutChangeTarget) {
      updateStatutMutation.mutate({
        id: statutChangeTarget.parking.id,
        statut: statutChangeTarget.newStatut,
      });
      setStatutChangeTarget(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
        <p className="text-sm text-gray-500 mt-3">Chargement des parkings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Gestion des parkings</h2>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary"
          >
            <FunnelIcon className="w-5 h-5 mr-2" />
            Filtres
          </button>
          <Link to="/carte" className="btn-secondary inline-flex items-center">
            <MapPinIcon className="w-5 h-5 mr-2" />
            Carte
          </Link>
          <button
            onClick={() => {
              setImportFile(null);
              setImportAeroport('');
              setImportResult(null);
              setShowImportModal(true);
            }}
            className="btn-secondary"
          >
            <ArrowUpTrayIcon className="w-5 h-5 mr-2" />
            Importer
          </button>
          <button
            onClick={() => {
              setEditingParking(null);
              setShowModal(true);
            }}
            className="btn-primary"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Nouveau parking
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card">
            <div className="card-body text-center">
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-sm text-gray-500">Total</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center">
              <p className="text-2xl font-bold text-green-600">{stats.disponibles}</p>
              <p className="text-sm text-gray-500">Disponibles</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center">
              <p className="text-2xl font-bold text-orange-600">{stats.enDeneignement}</p>
              <p className="text-sm text-gray-500">En deneignement</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center">
              <p className="text-2xl font-bold text-red-600">{stats.indisponibles}</p>
              <p className="text-sm text-gray-500">Indisponibles</p>
            </div>
          </div>
        </div>
      )}

      {/* Bandeau jalonnement Contact/Large (SFD) */}
      <div className="card border-l-4 border-l-blue-400">
        <div className="card-body">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-700">
              <span className="font-medium">Jalonnement :</span>
              <span><strong>Contact occupe</strong> = 5 jalons</span>
              <span><strong>Large occupe</strong> = 7 jalons</span>
              <span><strong>Non occupe</strong> = 1 jalon</span>
            </div>
            {stats && stats.sansSituation > 0 && (
              <button
                onClick={() => setFilters({ ...filters, situation: 'sans_situation' })}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 hover:bg-red-200"
                title="Filtrer les parkings sans situation"
              >
                <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                {stats.sansSituation} parking(s) sans situation
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filtres */}
      {showFilters && (
        <div className="card">
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="label">Aeroport</label>
                <select
                  className="input"
                  value={filters.aeroport || ''}
                  onChange={(e) =>
                    setFilters({ ...filters, aeroport: e.target.value || undefined })
                  }
                >
                  <option value="">Tous les aeroports</option>
                  {aeroports?.map((aero) => (
                    <option key={aero} value={aero}>
                      {aero}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Statut hiver</label>
                <select
                  className="input"
                  value={filters.statutHiver || ''}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      statutHiver: (e.target.value as StatutParking) || undefined,
                    })
                  }
                >
                  <option value="">Tous les statuts</option>
                  {Object.entries(STATUT_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Situation</label>
                <select
                  className="input"
                  value={filters.situation || ''}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      situation: (e.target.value as ParkingFilters['situation']) || undefined,
                    })
                  }
                >
                  <option value="">Toutes les situations</option>
                  {Object.entries(SITUATION_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                  <option value="sans_situation">Sans situation</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => setFilters({})}
                  className="btn-secondary w-full"
                >
                  Reinitialiser
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tableau */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Nom</th>
                <th>Aeroport</th>
                <th>Situation</th>
                <th>Statut hiver</th>
                <th>Actif</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {parkings?.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <MapPinIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500 mb-3">Aucun parking trouve</p>
                    <button
                      onClick={() => { setEditingParking(null); setShowModal(true); }}
                      className="btn-primary inline-flex items-center"
                    >
                      <PlusIcon className="w-5 h-5 mr-2" />
                      Creer un parking
                    </button>
                  </td>
                </tr>
              ) : (
                parkings?.map((parking) => (
                  <tr key={parking.id}>
                    <td className="font-medium">{parking.code}</td>
                    <td>{parking.nom || '-'}</td>
                    <td>
                      <div className="flex items-center">
                        <MapPinIcon className="w-4 h-4 mr-1 text-gray-400" />
                        {parking.aeroport}
                      </div>
                    </td>
                    <td>
                      {parking.situation ? (
                        <span className={`px-2 py-1 text-xs rounded-full ${SITUATION_COLORS[parking.situation]}`}>
                          {SITUATION_LABELS[parking.situation]}
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-red-100 text-red-800"
                          title="Ce parking ne peut pas etre choisi pour une nouvelle intervention tant que sa situation n'est pas renseignee"
                        >
                          <ExclamationTriangleIcon className="w-3.5 h-3.5 mr-1" />
                          Sans situation
                        </span>
                      )}
                    </td>
                    <td>
                      <select
                        value={parking.statutHiver}
                        onChange={(e) =>
                          handleStatutChange(parking, e.target.value as StatutParking)
                        }
                        className={`text-xs px-2 py-1 rounded-full border-0 cursor-pointer ${
                          STATUT_COLORS[parking.statutHiver]
                        }`}
                      >
                        {Object.entries(STATUT_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          parking.actif
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {parking.actif ? 'Oui' : 'Non'}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setEditingParking(parking);
                            setShowModal(true);
                          }}
                          className="p-1 text-gray-400 hover:text-primary-500"
                          title="Modifier"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(parking)}
                          className="p-1 text-gray-400 hover:text-red-500"
                          title="Supprimer"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modale confirmation suppression */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title={`Supprimer le parking "${deleteTarget?.code}" ?`}
        message="Cette action est irreversible. Le parking sera definitivement supprime."
        confirmLabel="Supprimer"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      {/* Modale confirmation changement statut */}
      <ConfirmModal
        isOpen={!!statutChangeTarget}
        onClose={() => setStatutChangeTarget(null)}
        onConfirm={confirmStatutChange}
        title="Changer le statut du parking ?"
        message={statutChangeTarget
          ? `Changer le statut du parking "${statutChangeTarget.parking.code}" de "${STATUT_LABELS[statutChangeTarget.parking.statutHiver]}" a "${STATUT_LABELS[statutChangeTarget.newStatut]}" ?`
          : ''}
        confirmLabel="Confirmer"
        variant="warning"
        isLoading={updateStatutMutation.isPending}
      />

      {/* Modal creation/edition */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingParking ? 'Modifier le parking' : 'Nouveau parking'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label">Code *</label>
                <input
                  type="text"
                  name="code"
                  defaultValue={editingParking?.code}
                  className="input"
                  required
                  maxLength={10}
                />
              </div>
              <div>
                <label className="label">Nom</label>
                <input
                  type="text"
                  name="nom"
                  defaultValue={editingParking?.nom}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Aeroport *</label>
                <select
                  name="aeroport"
                  defaultValue={editingParking?.aeroport || 'CDG'}
                  className="input"
                  required
                >
                  <option value="CDG">Paris-CDG</option>
                  <option value="ORY">Paris-Orly</option>
                  <option value="LBG">Le Bourget</option>
                </select>
              </div>
              <div>
                <label className="label">Statut hiver</label>
                <select
                  name="statutHiver"
                  defaultValue={editingParking?.statutHiver || 'disponible'}
                  className="input"
                >
                  {Object.entries(STATUT_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Situation</label>
                <select
                  name="situation"
                  defaultValue={editingParking?.situation || ''}
                  className="input"
                >
                  <option value="">Non definie</option>
                  {Object.entries(SITUATION_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-400">
                  Obligatoire pour qu'une nouvelle intervention puisse etre creee sur ce parking
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Latitude</label>
                  <input
                    type="number"
                    step="any"
                    name="latitude"
                    defaultValue={editingParking?.latitude ?? ''}
                    className="input"
                    placeholder="48.7258833"
                  />
                </div>
                <div>
                  <label className="label">Longitude</label>
                  <input
                    type="number"
                    step="any"
                    name="longitude"
                    defaultValue={editingParking?.longitude ?? ''}
                    className="input"
                    placeholder="2.3606417"
                  />
                </div>
                <p className="col-span-2 -mt-1 text-xs text-gray-400">
                  Optionnel - utilise pour l'affichage sur la carte
                </p>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingParking(null);
                  }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editingParking ? 'Modifier' : 'Creer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal import Excel */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Importer des parkings</h3>

            {!importResult ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Fichier Excel ou CSV avec colonnes : <strong>Code</strong> (ou <strong>Poste</strong>) |{' '}
                  <strong>Nom</strong> | <strong>Aeroport</strong> | <strong>Situation</strong> (ou{' '}
                  <strong>Type de Poste</strong>, optionnelle, CONTACT ou LARGE) | <strong>Latitude/Longitude
                  (decimal)</strong> (optionnelles, pour la carte)
                </p>
                <div>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                  />
                </div>
                <div>
                  <label className="label">Aeroport par defaut</label>
                  <input
                    type="text"
                    value={importAeroport}
                    onChange={(e) => setImportAeroport(e.target.value.toUpperCase())}
                    className="input"
                    placeholder="ORY"
                    maxLength={10}
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    Utilise pour les lignes sans colonne Aeroport (referentiels mono-aeroport type "postes LFPO")
                  </p>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowImportModal(false)}
                    className="btn-secondary"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={!importFile || importMutation.isPending}
                    className="btn-primary"
                  >
                    {importMutation.isPending ? 'Import en cours...' : 'Importer'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">{importResult.created}</p>
                    <p className="text-sm text-green-700">Cree(s)</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-600">{importResult.updated}</p>
                    <p className="text-sm text-blue-700">Mis a jour</p>
                  </div>
                </div>
                {importResult.errors.length > 0 && (
                  <div className="bg-red-50 rounded-lg p-3">
                    <p className="text-sm font-semibold text-red-700 mb-2">
                      {importResult.errors.length} erreur(s) :
                    </p>
                    <ul className="text-sm text-red-600 space-y-1 max-h-40 overflow-y-auto">
                      {importResult.errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex justify-end pt-4">
                  <button
                    onClick={() => setShowImportModal(false)}
                    className="btn-primary"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ParkingsPage;
